/**
 * The central audit viewer's source registry (docs/AUDIT.md §10a).
 *
 * `public.org_audit_events` unions every org-scoped, member-readable `_audit`
 * table into one §3-shaped row. This registry is the TypeScript half of that
 * view: one entry per view arm, keyed by the arm's `source_table` — the
 * **audit table's own name** (`expenses_audit`, `asset_audit`,
 * `budget_allocation_audit`, …), so a key corresponds 1:1 to a physical table and the
 * legacy `account_audit` / `asset_audit` / `budget_allocation_audit` names need
 * no special case. Each entry says:
 *
 *   - `entityType` — what one audited row is (the view's `entity_type`);
 *   - `entityColumn` — the audit table's entity-id column;
 *   - `rootTypes` — the record(s) a child row belongs to (the view's
 *     `root_type`; empty means the row is its own root, `is_root = true`);
 *   - `label(snapshot)` — a human label read from the row snapshot, so it works
 *     for deleted rows (no lookup); `null` when the snapshot carries nothing
 *     readable (the viewer falls back to the root's ref label or a short uuid);
 *   - `hiddenFields` — columns the diff never shows: ids, timestamps and the
 *     table's GENERATED columns (a generated column changes whenever its inputs
 *     do, so it would only repeat the real change — plan A.6);
 *   - `fieldKinds` — how a value renders: `money`, `day` (a calendar-day
 *     `date` column — never through `new Date`, docs/DATES.md), `instant`
 *     (`timestamptz`), `bool`, `json`, or `ref:<kind>` (a uuid resolved to a
 *     label through {@link AUDIT_REF_TABLES}). A column with no kind renders as
 *     plain text / number; any other `*_id` renders as a short uuid.
 *
 * Excluded tables (never arms — see the view header and the coverage test):
 * `organization_audit_log`, `time_entry_audit_log`,
 * `bank_connection_activity_audit`, `bookkeeper_pii_tokens_audit`,
 * `usage_catalog_audit`, `organization_deletion_audit`,
 * `gdpr_personal_data_access_log`, `platform_user_audit` (super-admin
 * grants/revokes and user deactivate / reactivate / delete, 20261027200000 —
 * platform-level, `organization_id` always NULL, super-admin-only SELECT, so
 * it has no place in an org's log), `tax_rate_templates_audit` (global
 * reference data, 20261029100000 — `organization_id` always NULL,
 * super-admin-only SELECT; the org rows its templates feed are audited in
 * `tax_rates_audit`, which is an arm).
 */
/** A uuid-valued column that the viewer resolves to a label. */
export const AUDIT_REF_KINDS = [
    "account",
    "transaction",
    "project",
    "business",
    "person",
    "fund",
    "place",
    "asset",
    "budget",
    "budget_group",
    "budget_category",
    "tax_rate",
    "import",
    "expense",
    "ap_bill",
    "ar_invoice",
    "user",
];
const str = (v) => {
    if (v === null || v === undefined)
        return null;
    const s = String(v).trim();
    return s === "" ? null : s;
};
const first = (...values) => {
    for (const v of values) {
        const s = str(v);
        if (s !== null)
            return s;
    }
    return null;
};
/** The `en` text of a localized jsonb value (`{ en, nl?, fr? }` — Level Up content). */
const localizedEn = (v) => v !== null && typeof v === "object" ? str(v.en) : null;
/** `"<number> – <name>"` (en dash), or whichever half exists. */
const numberDashName = (num, name) => {
    const n = str(num);
    const m = str(name);
    if (n && m)
        return `${n} – ${m}`;
    return n ?? m;
};
/**
 * Ref kind → table. `user` is absent on purpose: actors resolve through
 * `profiles` (keyed by `user_id`) in the API's `resolveActorNames`, which also
 * returns the email fallback.
 */
export const AUDIT_REF_TABLES = {
    account: {
        table: "chart_of_accounts",
        select: "id, account_number, account_name",
        label: (r) => numberDashName(r.account_number, r.account_name),
    },
    transaction: {
        table: "ledger_transactions",
        select: "id, transaction_number",
        label: (r) => str(r.transaction_number),
    },
    project: { table: "projects", select: "id, name, code", label: (r) => first(r.name, r.code) },
    business: { table: "businesses", select: "id, name, code", label: (r) => first(r.name, r.code) },
    person: {
        table: "people",
        select: "id, display_name, first_name, last_name, code",
        label: (r) => first(r.display_name, [str(r.first_name), str(r.last_name)].filter(Boolean).join(" "), r.code),
    },
    fund: {
        table: "funds",
        select: "id, fund_code, fund_name",
        label: (r) => numberDashName(r.fund_code, r.fund_name),
    },
    place: { table: "places", select: "id, name, code", label: (r) => first(r.name, r.code) },
    asset: { table: "assets", select: "id, name, code", label: (r) => first(r.name, r.code) },
    budget: { table: "envelope_budgets", select: "id, name", label: (r) => str(r.name) },
    budget_group: { table: "budget_category_groups", select: "id, name", label: (r) => str(r.name) },
    budget_category: { table: "budget_categories", select: "id, name", label: (r) => str(r.name) },
    tax_rate: { table: "tax_rates", select: "id, name", label: (r) => str(r.name) },
    import: {
        table: "accounting_imports",
        select: "id, booking_date, description, related_party_name",
        label: (r) => first(r.description, r.related_party_name, r.booking_date),
    },
    expense: {
        table: "expenses",
        select: "id, expense_number, description",
        label: (r) => first(r.expense_number, r.description),
    },
    ap_bill: {
        table: "accounts_payable",
        select: "id, invoice_number, description",
        label: (r) => first(r.invoice_number, r.description),
    },
    ar_invoice: {
        table: "accounts_receivable",
        select: "id, invoice_number, description",
        label: (r) => first(r.invoice_number, r.description),
    },
};
/** Root types the view emits (`root_type`), i.e. the records a change set can belong to. */
export const AUDIT_ROOT_TYPES = [
    "expense",
    "ap_bill",
    "ar_invoice",
    "accounting_import",
    "bank_connection",
    "business",
    "carbon_meter_type_factor_binding",
    "carbon_usage_factor_binding",
    "channel_payout",
    "ledger_transaction",
    "nl_wkr_entry",
    "person",
    "place",
    "place_classification_detail_requirement",
    "place_detail_definition",
    "place_utility_account",
    "project",
    "asset",
    "recurring_document_template",
    "sales_channel",
    "account",
    "budget",
    "fund",
    "volunteer_assignment",
    "level_up_scorecard",
    "level_up_version",
    "level_up_metric",
    "level_up_terms",
    "level_up_enrollment",
    "level_up_invitation",
    "tax_rate",
    "subscription",
];
/**
 * Root type → the ref kind that labels it, so a change set whose root row is
 * absent (a lines-only save skips the `{updated_at}`-only header row; the org
 * list only carries `root_type`/`root_id`) can still be named. Roots with no
 * entry fall back to the type label + a short uuid.
 */
export const AUDIT_ROOT_REF_KIND = {
    expense: "expense",
    ap_bill: "ap_bill",
    ar_invoice: "ar_invoice",
    accounting_import: "import",
    business: "business",
    ledger_transaction: "transaction",
    person: "person",
    place: "place",
    project: "project",
    asset: "asset",
    account: "account",
    budget: "budget",
    fund: "fund",
    tax_rate: "tax_rate",
};
/**
 * Record types the per-record History endpoint resolves
 * (`GET /api/v1/audit/orgs/:orgId/records/:recordType/:recordId`).
 */
export const AUDIT_RECORD_TYPES = [
    "expense",
    "ap_bill",
    "ar_invoice",
    "ledger_transaction",
    "account",
    "asset",
    "budget",
    "place",
    "fund",
    "level_up_metric",
    "level_up_version",
];
/**
 * Platform record types — History for records that belong to no org, served
 * by `GET /api/v1/audit/platform/records/:recordType/:recordId` (super admins
 * only). `platform_user` reads `platform_user_audit` (one user's super-admin
 * grants and revokes, deactivations, reactivations and deletion). Deliberately NOT in {@link AUDIT_RECORD_TYPES} — the
 * org route keeps answering 400 for them — and not a `SOURCES` entry: that
 * list is the org log's arms and source filter.
 */
export const PLATFORM_AUDIT_RECORD_TYPES = ["platform_user"];
export function isPlatformAuditRecordType(type) {
    return PLATFORM_AUDIT_RECORD_TYPES.includes(type);
}
/**
 * Record types whose History is accounting data — refused (403
 * `area_forbidden`) to a caller whose org role lacks the `accounting` scope
 * (`orgRoleHasScope`, org roles step 2). `asset` and `place` stay open.
 */
export const AUDIT_FINANCIAL_RECORD_TYPES = [
    "expense",
    "ap_bill",
    "ar_invoice",
    "ledger_transaction",
    "account",
    "budget",
    "fund",
];
export function isFinancialAuditRecordType(type) {
    return AUDIT_FINANCIAL_RECORD_TYPES.includes(type);
}
/** Hidden for every source: identity, org and row timestamps. */
export const AUDIT_COMMON_HIDDEN_FIELDS = [
    "id",
    "organization_id",
    "created_at",
    "updated_at",
];
/** Kinds shared by every source unless the source overrides the column. */
export const AUDIT_COMMON_FIELD_KINDS = {
    created_by: "ref:user",
    updated_by: "ref:user",
    created_at: "instant",
    updated_at: "instant",
};
function kinds(spec) {
    const out = {};
    for (const c of spec.money ?? [])
        out[c] = "money";
    for (const c of spec.day ?? [])
        out[c] = "day";
    for (const c of spec.instant ?? [])
        out[c] = "instant";
    for (const c of spec.bool ?? [])
        out[c] = "bool";
    for (const c of spec.json ?? [])
        out[c] = "json";
    for (const [c, k] of Object.entries(spec.refs ?? {}))
        out[c] = `ref:${k}`;
    return out;
}
function source(input) {
    const { generated, kinds: spec, ...rest } = input;
    return {
        ...rest,
        hiddenFields: [...AUDIT_COMMON_HIDDEN_FIELDS, ...(generated ?? [])],
        fieldKinds: { ...AUDIT_COMMON_FIELD_KINDS, ...kinds(spec ?? {}) },
    };
}
const SOURCES = [
    // ---- Phase A documents --------------------------------------------------
    source({
        sourceTable: "expenses_audit",
        entityType: "expense",
        entityColumn: "expense_id",
        rootTypes: [],
        label: (s) => first(s.expense_number, s.description),
        kinds: {
            money: ["total_amount", "original_amount"],
            day: ["expense_date", "exchange_rate_date"],
            json: ["metadata"],
            refs: {
                business_id: "business",
                person_id: "person",
                project_id: "project",
                ledger_transaction_id: "transaction",
            },
        },
    }),
    source({
        sourceTable: "accounts_payable_audit",
        entityType: "ap_bill",
        entityColumn: "accounts_payable_id",
        rootTypes: [],
        label: (s) => first(s.invoice_number, s.description),
        generated: ["amount_remaining"],
        kinds: {
            money: ["amount_total", "amount_paid", "amount_remaining", "original_amount"],
            day: ["invoice_date", "due_date", "exchange_rate_date"],
            instant: ["approved_at", "cancelled_at", "rejected_at"],
            json: ["metadata"],
            refs: {
                business_id: "business",
                person_id: "person",
                project_id: "project",
                ledger_transaction_id: "transaction",
                void_transaction_id: "transaction",
                approved_by: "user",
                cancelled_by: "user",
                rejected_by: "user",
            },
        },
    }),
    source({
        sourceTable: "accounts_receivable_audit",
        entityType: "ar_invoice",
        entityColumn: "accounts_receivable_id",
        rootTypes: [],
        label: (s) => first(s.invoice_number, s.description),
        generated: ["amount_outstanding"],
        kinds: {
            money: ["amount_total", "amount_received", "amount_outstanding", "original_amount"],
            day: ["invoice_date", "due_date", "exchange_rate_date"],
            instant: ["voided_at"],
            json: ["metadata"],
            refs: {
                business_id: "business",
                person_id: "person",
                project_id: "project",
                ledger_transaction_id: "transaction",
                void_transaction_id: "transaction",
                voided_by: "user",
            },
        },
    }),
    source({
        // Root is per row: `document_type` ('ar' | 'ap' | 'expense') → ar_invoice /
        // ap_bill / expense, root id `document_id`.
        sourceTable: "invoice_line_items_audit",
        entityType: "invoice_line_item",
        entityColumn: "invoice_line_item_id",
        rootTypes: ["ar_invoice", "ap_bill", "expense"],
        label: (s) => str(s.description),
        generated: ["line_total", "tax_amount", "line_total_with_tax"],
        kinds: {
            money: ["unit_price", "line_total", "tax_amount", "line_total_with_tax"],
            bool: ["tax_inclusive"],
            refs: {
                revenue_account_id: "account",
                expense_account_id: "account",
                tax_rate_id: "tax_rate",
                expense_id: "expense",
                source_expense_id: "expense",
            },
        },
    }),
    source({
        sourceTable: "accounting_import_matches_audit",
        entityType: "accounting_import_match",
        entityColumn: "accounting_import_match_id",
        rootTypes: ["accounting_import"],
        label: () => null,
        kinds: {
            money: ["allocated_amount"],
            refs: {
                import_id: "import",
                transaction_id: "transaction",
                payment_transaction_id: "transaction",
                ap_invoice_id: "ap_bill",
                ar_invoice_id: "ar_invoice",
            },
        },
    }),
    // ---- Funds (fund management P1, 20261006210000) ---------------------------
    source({
        sourceTable: "funds_audit",
        entityType: "fund",
        entityColumn: "fund_id",
        rootTypes: [],
        label: (s) => numberDashName(s.fund_code, s.fund_name),
        generated: ["is_active"],
        kinds: {
            money: ["requested_amount"],
            day: ["period_start", "period_end", "award_date"],
            refs: {
                project_id: "project",
                funder_business_id: "business",
                receivable_account_id: "account",
                deferred_account_id: "account",
                income_account_id: "account",
            },
        },
    }),
    source({
        // Root = the parent fund (the view's root_id is the snapshot fund_id).
        sourceTable: "fund_installments_audit",
        entityType: "fund_installment",
        entityColumn: "fund_installment_id",
        rootTypes: ["fund"],
        label: (s) => (s.sequence == null ? null : `#${String(s.sequence)}`),
        kinds: {
            money: ["amount"],
            day: ["expected_date"],
            refs: { fund_id: "fund", ar_invoice_id: "ar_invoice" },
        },
    }),
    // ---- Tax rates (Vermont local rates, 20261028100500) ----------------------
    source({
        // Self-rooted. Written by sync_org_tax_rates (town added / removed: new
        // combined rates, re-windowed or expired ones), custom-rate create /
        // deactivate and the default-rate switch. template_id /
        // local_template_id name global templates (shown as short ids).
        sourceTable: "tax_rates_audit",
        entityType: "tax_rate",
        entityColumn: "tax_rate_id",
        rootTypes: [],
        label: (s) => str(s.name),
        kinds: {
            day: ["effective_from", "effective_to"],
            bool: ["is_active", "is_system", "is_default", "is_recoverable"],
            refs: { tax_collection_account_id: "account" },
        },
    }),
    // ---- Volunteer signup (20261008130000, AUDIT.md §6.16) -------------------
    source({
        // Self-rooted: the snapshot allowlist is id, shift_id, volunteer_id,
        // source, status, cancel_reason, over_capacity, created_by, created_at —
        // no readable name (volunteer PII must not outlive erasure). Anonymous
        // self rows audit as actor_type='backend', user_id NULL, source='self'.
        sourceTable: "volunteer_assignments_audit",
        entityType: "volunteer_assignment",
        entityColumn: "volunteer_assignment_id",
        rootTypes: [],
        label: () => null,
        kinds: {
            bool: ["over_capacity"],
        },
    }),
    // ---- Level Up (20261013120000, AUDIT.md §6, docs/LEVEL_UP.md) ------------
    // Owner-side content rows denormalise `owner_organization_id` (and tiers
    // `version_id`); both are hidden like the generated tier `points`.
    source({
        // Root = the scorecard; org = the school.
        sourceTable: "level_up_answers_audit",
        entityType: "level_up_answer",
        entityColumn: "answer_id",
        rootTypes: ["level_up_scorecard"],
        label: (s) => (s.selected_tier == null ? "—" : `Tier ${String(s.selected_tier)}`),
    }),
    source({
        // Self-rooted, audited in the SCHOOL's org (a GSA actor shows as a user the
        // school cannot resolve).
        sourceTable: "level_up_enrollments_audit",
        entityType: "level_up_enrollment",
        entityColumn: "enrollment_id",
        rootTypes: [],
        label: (s) => str(s.school_name),
        generated: ["owner_organization_id"],
        kinds: {
            instant: ["accepted_at", "ended_at"],
            refs: { invited_by: "user", accepted_by: "user", ended_by: "user" },
        },
    }),
    source({
        // Self-rooted (Phase 2 builder): status, metadata (tier names, badge
        // bands, help links) and publishing.
        sourceTable: "level_up_versions_audit",
        entityType: "level_up_version",
        entityColumn: "version_id",
        rootTypes: [],
        label: (s) => (s.version == null ? null : `v${String(s.version)}`),
        generated: ["owner_organization_id"],
        kinds: { instant: ["published_at"], json: ["metadata"], refs: { published_by: "user" } },
    }),
    source({
        // Phase 2 builder: root = the version.
        sourceTable: "level_up_pillars_audit",
        entityType: "level_up_pillar",
        entityColumn: "pillar_id",
        rootTypes: ["level_up_version"],
        label: (s) => localizedEn(s.name),
        generated: ["owner_organization_id"],
        kinds: { json: ["name"] },
    }),
    source({
        sourceTable: "level_up_metrics_audit",
        entityType: "level_up_metric",
        entityColumn: "metric_id",
        rootTypes: ["level_up_version"],
        label: (s) => localizedEn(s.focus),
        generated: ["owner_organization_id"],
        kinds: { json: ["subcategory", "focus", "description", "links"] },
    }),
    source({
        sourceTable: "level_up_tiers_audit",
        entityType: "level_up_tier",
        entityColumn: "tier_id",
        rootTypes: ["level_up_metric"],
        label: (s) => localizedEn(s.name),
        generated: ["owner_organization_id", "version_id", "points"],
        kinds: {
            bool: ["is_empty", "evidence_required"],
            json: ["name", "label", "statement", "how_to_links", "frameworks"],
        },
    }),
    source({
        sourceTable: "level_up_terms_audit",
        entityType: "level_up_terms",
        entityColumn: "terms_id",
        rootTypes: [],
        label: (s) => (s.version == null ? null : `v${String(s.version)}`),
        generated: ["owner_organization_id"],
        kinds: { instant: ["published_at"], json: ["body"], refs: { published_by: "user" } },
    }),
    source({
        // Allowlist snapshot (`level_up_invitations_audit_snapshot`): never `email`
        // or `token_hash` — a resend still lists `token_hash` in changed_fields.
        sourceTable: "level_up_invitations_audit",
        entityType: "level_up_invitation",
        entityColumn: "invitation_id",
        rootTypes: [],
        label: (s) => str(s.school_name),
        generated: ["owner_organization_id"],
        kinds: {
            instant: ["expires_at", "accepted_at", "revoked_at"],
            refs: { invited_by: "user" },
        },
    }),
    // ---- Bank feed ------------------------------------------------------------
    source({
        sourceTable: "accounting_imports_audit",
        entityType: "accounting_import",
        entityColumn: "accounting_import_id",
        rootTypes: [],
        label: (s) => first(s.description, s.related_party_name, s.booking_date),
        generated: ["signed_amount"],
        kinds: {
            money: ["amount", "signed_amount"],
            day: ["booking_date", "value_date"],
            instant: ["processed_at"],
            refs: {
                bank_account_id: "account",
                suggested_account_id: "account",
                created_transaction_id: "transaction",
                fund_id: "fund",
                project_id: "project",
                processed_by: "user",
                duplicate_of_import_id: "import",
            },
        },
    }),
    source({
        sourceTable: "bank_connections_audit",
        entityType: "bank_connection",
        entityColumn: "bank_connection_id",
        rootTypes: [],
        label: (s) => str(s.aspsp_name),
        kinds: { instant: ["valid_until", "authorized_at", "revoked_at"] },
    }),
    source({
        sourceTable: "bank_connection_accounts_audit",
        entityType: "bank_connection_account",
        entityColumn: "bank_connection_account_id",
        rootTypes: ["bank_connection"],
        label: (s) => first(s.display_name, s.iban, s.account_uid),
        kinds: {
            money: ["last_balance_amount"],
            day: ["last_balance_reference_date"],
            instant: ["last_synced_at", "last_balance_at"],
            bool: ["flip_card_sign"],
            refs: { chart_of_accounts_id: "account" },
        },
    }),
    // ---- Businesses ---------------------------------------------------------
    source({
        sourceTable: "businesses_audit",
        entityType: "business",
        entityColumn: "business_id",
        rootTypes: [],
        label: (s) => first(s.name, s.code),
        kinds: { refs: { primary_contact_id: "person", billing_contact_id: "person" } },
    }),
    source({
        sourceTable: "business_contracts_audit",
        entityType: "business_contract",
        entityColumn: "contract_id",
        rootTypes: ["business"],
        label: (s) => first(s.contract_name, s.contract_number),
        kinds: {
            money: ["contract_value"],
            day: ["start_date", "end_date", "renewal_date"],
            bool: ["auto_renewal"],
            json: ["key_terms", "metadata"],
            refs: { business_id: "business", project_id: "project" },
        },
    }),
    source({
        sourceTable: "business_vendor_identity_audit",
        entityType: "business_vendor_identity",
        entityColumn: "business_vendor_identity_id",
        rootTypes: ["business"],
        label: (s) => str(s.trading_name),
        kinds: { bool: ["is_related_party"], refs: { business_id: "business" } },
    }),
    // ---- Carbon bindings (org rows only) --------------------------------------
    source({
        sourceTable: "carbon_meter_type_factor_bindings_audit",
        entityType: "carbon_meter_type_factor_binding",
        entityColumn: "binding_id",
        rootTypes: [],
        label: (s) => str(s.meter_type_code),
        kinds: { bool: ["is_active"], json: ["dimension_filter"] },
    }),
    source({
        sourceTable: "carbon_usage_factor_bindings_audit",
        entityType: "carbon_usage_factor_binding",
        entityColumn: "binding_id",
        rootTypes: [],
        label: (s) => str(s.usage_key),
        kinds: { bool: ["is_active"], json: ["dimension_filter"] },
    }),
    // ---- Sales channels -----------------------------------------------------
    source({
        sourceTable: "channel_payouts_audit",
        entityType: "channel_payout",
        entityColumn: "channel_payout_id",
        rootTypes: [],
        label: (s) => str(s.payout_number),
        generated: ["net_amount"],
        kinds: {
            money: ["gross_amount", "fee_amount", "net_amount"],
            day: ["payout_date"],
            instant: ["voided_at"],
            refs: {
                bank_account_id: "account",
                clearing_account_id: "account",
                fee_account_id: "account",
                settlement_transaction_id: "transaction",
                void_transaction_id: "transaction",
                voided_by: "user",
            },
        },
    }),
    source({
        sourceTable: "channel_payout_allocations_audit",
        entityType: "channel_payout_allocation",
        entityColumn: "channel_payout_allocation_id",
        rootTypes: ["channel_payout"],
        label: () => null,
        kinds: {
            money: ["amount"],
            refs: { ar_invoice_id: "ar_invoice", receipt_transaction_id: "transaction" },
        },
    }),
    source({
        sourceTable: "sales_channels_audit",
        entityType: "sales_channel",
        entityColumn: "sales_channel_id",
        rootTypes: [],
        label: (s) => str(s.name),
        kinds: { bool: ["is_active"], refs: { clearing_account_id: "account" } },
    }),
    source({
        // Stripe payment links (20261008110000). Root = the parent channel (the
        // view's root_id is the audit table's sales_channel_id lookup column).
        sourceTable: "sales_channel_payment_links_audit",
        entityType: "sales_channel_payment_link",
        entityColumn: "payment_link_id",
        rootTypes: ["sales_channel"],
        label: (s) => str(s.label),
        kinds: {
            money: ["amount", "preset_amount"],
            bool: ["is_active"],
            refs: { revenue_account_id: "account", tax_rate_id: "tax_rate", fund_id: "fund" },
        },
    }),
    // ---- Subscriptions (20261030120000, docs/SUBSCRIPTIONS.md) --------------
    source({
        // Self-rooted, one row per org: the entity id IS organization_id (the
        // parent's key). stripe_event_created is the webhook's ordering stamp —
        // bookkeeping, hidden like the row timestamps.
        sourceTable: "subscription_org_state_audit",
        entityType: "subscription",
        entityColumn: "organization_id",
        rootTypes: [],
        label: (s) => str(s.plan_code),
        generated: ["stripe_event_created"],
        kinds: {
            instant: ["trial_ends_at", "current_period_end", "manual_ends_at"],
            bool: ["cancel_at_period_end"],
        },
    }),
    // ---- Ledger -------------------------------------------------------------
    source({
        sourceTable: "ledger_transactions_audit",
        entityType: "ledger_transaction",
        entityColumn: "ledger_transaction_id",
        rootTypes: [],
        label: (s) => first(s.transaction_number, s.description),
        kinds: {
            money: ["total_amount"],
            day: ["transaction_date"],
            refs: {
                project_id: "project",
                business_id: "business",
                fund_id: "fund",
                person_id: "person",
                ap_invoice_id: "ap_bill",
                ar_invoice_id: "ar_invoice",
                void_of_transaction_id: "transaction",
            },
        },
    }),
    source({
        sourceTable: "ledger_entries_audit",
        entityType: "ledger_entry",
        entityColumn: "ledger_entry_id",
        rootTypes: ["ledger_transaction"],
        label: (s) => str(s.description),
        kinds: {
            money: ["debit_amount", "credit_amount"],
            bool: ["is_reverse_charge"],
            json: ["custom_fields"],
            refs: {
                transaction_id: "transaction",
                account_id: "account",
                fund_id: "fund",
                tax_rate_id: "tax_rate",
            },
        },
    }),
    source({
        sourceTable: "nl_wkr_entries_audit",
        entityType: "nl_wkr_entry",
        entityColumn: "nl_wkr_entry_id",
        rootTypes: [],
        label: (s) => str(s.category),
        kinds: { money: ["amount"], day: ["entry_date"] },
    }),
    // ---- People (allowlist snapshot: no name — label by code) -----------------
    source({
        sourceTable: "people_audit",
        entityType: "person",
        entityColumn: "person_id",
        rootTypes: [],
        label: (s) => str(s.code),
        kinds: {
            instant: ["gdpr_erased_at"],
            bool: ["has_system_access", "is_business"],
            refs: { auth_user_id: "user", linked_business_id: "business" },
        },
    }),
    // ---- Places ---------------------------------------------------------------
    source({
        sourceTable: "places_audit",
        entityType: "place",
        entityColumn: "place_id",
        rootTypes: [],
        label: (s) => first(s.name, s.code),
        kinds: { bool: ["is_active"], refs: { parent_place_id: "place" } },
    }),
    source({
        sourceTable: "place_certifications_audit",
        entityType: "place_certification",
        entityColumn: "place_certification_id",
        rootTypes: ["place"],
        label: (s) => [str(s.scheme), str(s.level)].filter(Boolean).join(" ") || null,
        kinds: { day: ["issue_date", "expiry_date"], refs: { place_id: "place" } },
    }),
    source({
        sourceTable: "place_classification_detail_requirements_audit",
        entityType: "place_classification_detail_requirement",
        entityColumn: "requirement_id",
        rootTypes: [],
        label: (s) => str(s.occupancy_classification),
        kinds: { bool: ["is_hidden"] },
    }),
    source({
        sourceTable: "place_detail_definitions_audit",
        entityType: "place_detail_definition",
        entityColumn: "definition_id",
        rootTypes: [],
        label: (s) => first(s.name, s.key),
        kinds: {
            bool: ["requires_verification", "is_use_scoped", "is_active"],
            json: ["enum_options", "metadata"],
        },
    }),
    source({
        sourceTable: "place_details_audit",
        entityType: "place_detail",
        entityColumn: "place_detail_id",
        rootTypes: ["place"],
        label: () => null,
        kinds: {
            // `verified_at` is a plain `date` on this table, not an instant.
            day: ["value_date", "current_as_of", "verified_at"],
            bool: ["value_boolean", "is_retired", "is_estimated", "lived_experience_verified"],
            refs: { place_id: "place", verified_by_person_id: "person" },
        },
    }),
    source({
        sourceTable: "place_epc_audit",
        entityType: "place_epc",
        entityColumn: "place_epc_id",
        rootTypes: ["place"],
        label: (s) => first(s.epc_class, s.certificate_reference),
        kinds: { day: ["epc_issue_date", "epc_expiry_date"], refs: { place_id: "place" } },
    }),
    source({
        sourceTable: "place_tenures_audit",
        entityType: "place_tenure",
        entityColumn: "place_tenure_id",
        rootTypes: ["place"],
        label: (s) => str(s.tenure_type),
        kinds: {
            money: ["cost"],
            day: ["valid_from", "valid_to"],
            refs: { place_id: "place", landlord_person_id: "person" },
        },
    }),
    source({
        sourceTable: "place_uses_audit",
        entityType: "place_use",
        entityColumn: "place_use_id",
        rootTypes: ["place"],
        label: (s) => str(s.bedes_occupancy_classification),
        kinds: { bool: ["is_primary", "is_active"], refs: { place_id: "place" } },
    }),
    source({
        sourceTable: "place_utility_accounts_audit",
        entityType: "place_utility_account",
        entityColumn: "place_utility_account_id",
        rootTypes: ["place"],
        label: (s) => first(s.name, s.code, s.account_number),
        kinds: {
            bool: ["green_power"],
            json: ["metadata", "emission_factor_dimension_filter"],
            refs: { place_id: "place" },
        },
    }),
    source({
        sourceTable: "place_utility_readings_audit",
        entityType: "place_utility_reading",
        entityColumn: "place_utility_reading_id",
        rootTypes: ["place_utility_account"],
        label: (s) => {
            const from = str(s.period_start);
            const to = str(s.period_end);
            return from && to ? `${from} – ${to}` : (from ?? to);
        },
        kinds: {
            money: ["cost"],
            day: ["period_start", "period_end"],
            bool: ["is_estimated"],
            json: ["metadata"],
            refs: { ap_invoice_id: "ap_bill" },
        },
    }),
    // ---- Projects & assets ----------------------------------------------------
    source({
        sourceTable: "project_asset_assignments_audit",
        entityType: "project_asset_assignment",
        entityColumn: "assignment_id",
        rootTypes: ["asset"],
        label: (s) => first(s.asset_name, s.asset_code),
        kinds: {
            day: ["assigned_on", "returned_on"],
            refs: { project_id: "project", asset_id: "asset" },
        },
    }),
    source({
        sourceTable: "project_team_audit",
        entityType: "project_team_member",
        entityColumn: "project_team_id",
        rootTypes: ["project"],
        label: () => null,
        kinds: {
            day: ["start_date", "end_date"],
            refs: { project_id: "project", person_id: "person" },
        },
    }),
    source({
        sourceTable: "project_places_audit",
        entityType: "project_place",
        entityColumn: "project_place_id",
        rootTypes: ["project"],
        label: () => null,
        kinds: {
            day: ["start_date", "end_date"],
            bool: ["is_primary", "is_active"],
            refs: { project_id: "project", place_id: "place" },
        },
    }),
    source({
        // Also carries asset_attachments ATTACH / DETACH rows (payload has
        // `file_name` while the file exists) — see `auditEventLabel`.
        sourceTable: "asset_audit",
        entityType: "asset",
        entityColumn: "asset_id",
        rootTypes: [],
        label: (s) => first(s.name, s.code, s.file_name),
        kinds: {
            money: ["purchase_value", "current_value"],
            day: ["purchase_date"],
            refs: {
                assigned_project_id: "project",
                business_id: "business",
                purchase_expense_id: "expense",
                purchase_transaction_id: "transaction",
            },
        },
    }),
    // ---- Recurring documents --------------------------------------------------
    source({
        sourceTable: "recurring_document_templates_audit",
        entityType: "recurring_document_template",
        entityColumn: "recurring_document_template_id",
        rootTypes: [],
        label: (s) => str(s.name),
        generated: ["anchor_day"],
        kinds: {
            day: ["start_date", "end_date", "next_occurrence_date", "last_occurrence_date"],
            instant: ["last_generated_at"],
            bool: ["auto_issue"],
            json: ["payload"],
            refs: { owner_id: "user" },
        },
    }),
    // ---- Legacy logs converted/joined in Phase B ------------------------------
    source({
        sourceTable: "account_audit",
        entityType: "account",
        entityColumn: "account_id",
        rootTypes: [],
        label: (s) => numberDashName(s.account_number, s.account_name),
        generated: ["is_system_account"],
        kinds: {
            money: ["opening_balance", "current_balance"],
            day: ["opening_balance_date"],
            bool: ["is_active", "can_have_children", "requires_project", "is_system_account"],
            json: ["custom_fields"],
            refs: { parent_account_id: "account", default_tax_rate_id: "tax_rate" },
        },
    }),
    source({
        // Two kinds of row share this table: `budget_month_allocations` (month +
        // budgeted) and `budget_categories` (name, allocation_id = the category id).
        // entity_id = COALESCE(allocation_id, id); root = the budget.
        sourceTable: "budget_allocation_audit",
        entityType: "budget_allocation",
        entityColumn: "allocation_id",
        rootTypes: ["budget"],
        label: (s) => first(s.name, s.month),
        kinds: {
            money: ["budgeted", "total_budgeted", "default_budgeted"],
            day: ["month"],
            bool: ["is_hidden", "rollover_overspending"],
            refs: {
                budget_id: "budget",
                group_id: "budget_group",
                category_id: "budget_category",
                gl_account_id: "account",
            },
        },
    }),
];
/** Every view arm's `source_table`, in registry order. */
export const AUDIT_SOURCE_TABLES = SOURCES.map((s) => s.sourceTable);
export const AUDIT_SOURCES = Object.freeze(Object.fromEntries(SOURCES.map((s) => [s.sourceTable, s])));
export function isAuditSourceTable(value) {
    return Object.prototype.hasOwnProperty.call(AUDIT_SOURCES, value);
}
export function getAuditSource(sourceTable) {
    return isAuditSourceTable(sourceTable) ? AUDIT_SOURCES[sourceTable] : undefined;
}
/**
 * How a column renders. Unknown sources fall back to the common kinds, so a
 * row from an arm added to the view before the registry learns of it still
 * renders (as plain values).
 */
export function auditFieldKind(sourceTable, column) {
    const def = getAuditSource(sourceTable);
    return def ? def.fieldKinds[column] : AUDIT_COMMON_FIELD_KINDS[column];
}
export function isAuditFieldHidden(sourceTable, column) {
    const def = getAuditSource(sourceTable);
    return (def?.hiddenFields ?? AUDIT_COMMON_HIDDEN_FIELDS).includes(column);
}
/** `changed_fields` minus the hidden ones — what the diff actually shows. */
export function visibleChangedFields(sourceTable, fields) {
    return (fields ?? []).filter((f) => !isAuditFieldHidden(sourceTable, f));
}
/**
 * The label of one audit event, from its snapshot (`new_values`, else
 * `old_values`), so a deleted row is still named. `null` when the source is
 * unknown or the snapshot carries nothing readable.
 */
export function auditEventLabel(event) {
    const def = getAuditSource(event.source_table);
    if (!def)
        return null;
    const snapshot = event.new_values ?? event.old_values;
    return snapshot ? def.label(snapshot) : null;
}
/** First 8 characters of a uuid — the fallback for an unresolved id. */
export function shortAuditId(id) {
    return id ? id.slice(0, 8) : "";
}
//# sourceMappingURL=registry.js.map